import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, message, Modal, notification, Result } from "antd";

import { IUseCreateSignature } from "./IUseCreateSignature";
import { ProfileOutlined } from "@ant-design/icons";

export interface signaturitParams {
	id: number;
	paramName: string;
	paramValue: string;
};

export const useCreateSignature = ({
	translations,
	contractAPI,
	afinAPI,
	navigate,
	isInvestor,
	userIdInstitution,
	moduleRole
}: IUseCreateSignature) => {
	const [searchParams, _] = useSearchParams();

	const [contractData, setContractData]: any = useState();
	const [contractState, setContractState] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (searchParams.has("ContractId")) {
			getContractData(Number(searchParams.get("ContractId")));
		} else if (searchParams.has("hash")) {
			const hash = searchParams.get("hash");
			const getContractIdByHash = (hash: string) => (
				contractAPI.getContractByHash(hash)
					.then((res: any) => {
						if (res.success) {
							getContractData(Number(res.data));
						}
					})
				);
			getContractIdByHash(hash!);
		}
	}, [translations]);

	const getContractData = (contractId: number) => {
		contractAPI.getContract(contractId).then((res: any) => {
			if (res.success) {
				setContractData(res.data);
				setContractState(res.data.idContractState);
			}
			return res;
		});
	};

	const sendContractToAfin = () => {
		setLoading(true);
		let contractWithNewState = { ...contractData };
		afinAPI.sendContract(contractWithNewState.id)
			.then((res: any) => {
				if (res.success) {
					if (!isInvestor) {
						message.success(translations.contratoEnviado);
						goToHomepage();
					} else {
						Modal.success({
							title: translations.contractSentToAfin,
							content: translations.contratoEnviadoInversor,
							centered: true,
							okText: translations.close,
							cancelText: null,
							onOk() {
								window.location.href = 'https://qualitasfunds.com/';
							},
						});
					}
				}
			})
			.catch((err: any) => {
				notification.error({ message: "Error sending contract data to AFIN" });
			})
			.finally(() => setLoading(false));
	};

	const sendContractToComplianceRevison = () => {
		setLoading(true);
		let contractWithNewState = { ...contractData };
		contractWithNewState.idContractState = 16; // State: Compliance Revision
		contractAPI.updateContract(contractWithNewState).then((res: any) => {
			if (res.success) {
				if(!isInvestor) {
					message.success(translations.contratoEnviado);
					goToHomepage();
				}else{
					Modal.success({
						title: translations.contratoEnviado,
						content: translations.contratoEnviadoInversor,
						centered: true,
						okText: translations.close,
						cancelText: null,
						onOk() {
							window.location.href = 'https://qualitasfunds.com/';
						},
					});
				}
			}
		})
		.catch((err: any) =>
			notification.error({ message: "Contract couldn't be sent" })
		);
		setLoading(false);
	};

	const sendContractToComplianceValidated = () => {
		setLoading(true);
		let contractWithNewState = { ...contractData };
		contractWithNewState.idContractState = 17; // State: Compliance Validated
		contractAPI.updateContract(contractWithNewState).then((res: any) => {
			if (res.success) {
				if(!isInvestor) {
					message.success(translations.contratoEnviado);
					goToHomepage();
				}else{
					Modal.success({
						title: translations.contratoEnviado,
						content: translations.contratoEnviadoInversor,
						centered: true,
						okText: translations.close,
						cancelText: null,
						onOk() {
							window.location.href = 'https://qualitasfunds.com/';
						},
					});
				}
			}
		})
		.catch((err: any) =>
			notification.error({ message: "Contract couldn't be sent" })
		);
		setLoading(false);
	};

  	const goToHomepage = () => navigate("/");

	const getContent = () => {
		if (moduleRole === "Read") {
			const readOnlyTitle = contractState === 2
				? translations.contractReadyToSendTitle
				: userIdInstitution === 536
					? translations.contractReadyForAfinTitle
					: contractState === 1
						? translations.contractComplianceRevisionTitle
						: translations.contractReadyForQualitasTitle;

			return (
				<Result
					icon={<ProfileOutlined />}
					title={readOnlyTitle}
					extra={[
						<Button type="primary" key="console" onClick={goToHomepage}>
							{translations.inicio}
						</Button>,
					]}
				/>
			);
		}

		if (contractState === 2) {
			return (
				<Result
					icon={<ProfileOutlined />}
					title={translations.contractReadyToSendTitle}
				/>
			);
		} else if (userIdInstitution === 536) {
			return (
				<Result
					icon={<ProfileOutlined />}
					title={translations.contractReadyForAfinTitle}
					extra={
						<Button
							type="primary"
							onClick={sendContractToAfin}
							loading={loading}
						>
							{translations.sendContractToApproval}
						</Button>
					}
				/>
			);
		} else if (userIdInstitution !== 536 && moduleRole === "Manage" && (contractState === 16 || contractState === 1)) {
			return (
				<Result
					icon={<ProfileOutlined />}
					title={translations.contractReadyForQualitasTitle}
					extra={
						<Button
							type="primary"
							onClick={sendContractToComplianceValidated}
							loading={loading}
						>
							{translations.sendContractToApproval}
						</Button>
					}
				/>
			);
		} else if (userIdInstitution !== 536 && moduleRole === "Write" && contractState === 1) {
			return (
				<Result
					icon={<ProfileOutlined />}
					title={translations.contractComplianceRevisionTitle}
					extra={
						<Button
							type="primary"
							onClick={sendContractToComplianceRevison}
							loading={loading}
						>
							{translations.sendContractToApproval}
						</Button>
					}
				/>
			);
		} else {
			return (
				<Result
					status="success"
					title={translations.contratoEnviado}
					extra={[
						<Button type="primary" key="console" onClick={goToHomepage}>
							{translations.inicio}
						</Button>,
					]}
				/>
			);
		}
	};

	return {
		loading,
		getContent,
	};
};
